import clsx from 'clsx'
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner'
import PaginationBar from '../../components/PaginationBar/PaginationBar'
import { useContext, useEffect, useRef, useState } from 'react'
import {
  apiSlice,
  useDeletePortfoliosMutation,
  useDuplicatePortfolioMutation,
  usePortfoliosQuery,
} from '../../api/api'
import { useDispatch } from 'react-redux'
import IconMenu from '../../components/IconMenu/IconMenu'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as DuplicateIcon } from 'assets/icons/duplicate.svg'
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg'
import { ReactComponent as CaretDownIcon } from 'assets/icons/caret-down.svg'
import GeneralModal from '../../components/GeneralModal/GeneralModal'
import LoadingBlocker from '../../components/LoadingSpinner/LoadingBlocker'
import PortfolioList from './PortfolioList'
import useIsSubscribed from '../../utils/hooks/useIsSubscribed'
import PortfolioDuplicateModal from './PortfolioDuplicateModal'

const PortfoliosFrame = ({
  onEditPortfolio,
  onNewPortfolio,
  lockSelections,
  hideActions,
}) => {
  const dispatch = useDispatch()
  const ref = useRef(null)
  const {
    data: portfoliosData,
    isLoading,
    isFetching,
  } = usePortfoliosQuery({
    search: '',
    page: 1,
  })
  const [deletePortfolios, { isLoading: deleteLoading }] =
    useDeletePortfoliosMutation()
  const [duplicatePortfolio, { isLoading: duplicatePortfolioIsLoading }] =
    useDuplicatePortfolioMutation()
  const isSubscribed = useIsSubscribed()

  const [portfoliosToDelete, setPortfoliosToDelete] = useState([])
  const [portfolioToDuplicate, setPortfolioToDuplicate] = useState(false)
  const [selectedPortfolios, setSelectedPortfolios] = useState([])
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(
    window.localStorage.getItem('portfolioSort') || null,
  )

  const { data: portfolios, total, limit } = portfoliosData || {}
  const hasPendingPortfolio = portfoliosData?.data?.find(
    (portfolio) => portfolio.fileConversionResult === 'converting',
  )
  const searchFields = ['name', 'slug']
  const searchedPortfolios =
    portfolios?.filter(
      (portfolio) =>
        !search ||
        !!searchFields.find(
          (field) =>
            portfolio[field].toLowerCase().indexOf(search.toLowerCase()) > -1,
        ),
    ) || []
  const sortedPortfolios = sortPortfolios(searchedPortfolios, sort)
  const disableCreate = !isSubscribed && portfolios?.length > 0

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasPendingPortfolio) {
        dispatch(
          apiSlice.util.invalidateTags([{ type: 'Portfolios', id: 'LIST' }]),
        )
      }
    }, 15000)

    return () => clearInterval(interval)
  }, [portfoliosData])

  useEffect(() => {
    const clickEvent = (evt) => {
      if (!ref.current.contains(evt.target) && !lockSelections) {
        setSelectedPortfolios([])
      }
    }

    if (ref.current) {
      document.addEventListener('click', clickEvent)
    }
    return () => document.removeEventListener('click', clickEvent)
  }, [ref, lockSelections])

  useEffect(() => {
    const keydownEvent = (evt) => {
      const activeElement = document.activeElement
      const notInput =
        activeElement.tagName !== 'INPUT' &&
        activeElement.tagName !== 'TEXTAREA' &&
        document.activeElement.contentEditable !== 'true'
      if (
        selectedPortfolios.length > 0 &&
        notInput &&
        (evt.key === 'Delete' || evt.key === 'Backspace')
      ) {
        setPortfoliosToDelete(selectedPortfolios)
      }
    }

    document.addEventListener('keydown', keydownEvent)

    return () => {
      document.removeEventListener('keydown', keydownEvent)
    }
  }, [ref, JSON.stringify(selectedPortfolios)])

  useEffect(() => {
    setSelectedPortfolios(
      selectedPortfolios.filter(
        (portfolio) =>
          !!sortedPortfolios.find((search) => search.id === portfolio.id),
      ),
    )
  }, [search])

  function handlePageChange(newPage) {
    setPage(newPage)
  }

  function handlePortfolioClick(evt, portfolio) {
    if (evt.shiftKey) {
      const lastSelectedPortfolio = [...selectedPortfolios].pop()
      const portfolioIndex = sortedPortfolios.findIndex(
        (item) => item.id === portfolio.id,
      )
      const lastIndex = sortedPortfolios.findIndex(
        (item) => item.id === lastSelectedPortfolio.id,
      )
      if (portfolioIndex < lastIndex) {
        setSelectedPortfolios([
          ...selectedPortfolios,
          ...sortedPortfolios
            .slice(portfolioIndex, lastIndex)
            .filter(
              (item) =>
                !selectedPortfolios.find((search) => search.id === item.id),
            ),
        ])
      } else if (portfolioIndex > lastIndex) {
        setSelectedPortfolios([
          ...selectedPortfolios,
          ...sortedPortfolios
            .slice(lastIndex, portfolioIndex + 1)
            .filter(
              (item) =>
                !selectedPortfolios.find((search) => search.id === item.id),
            ),
        ])
      }
    } else {
      if (evt.ctrlKey || evt.metaKey) {
        if (selectedPortfolios.find((item) => item.id === portfolio.id)) {
          setSelectedPortfolios(
            selectedPortfolios.filter((item) => item.id !== portfolio.id),
          )
        } else {
          setSelectedPortfolios([...selectedPortfolios, portfolio])
        }
      } else {
        setSelectedPortfolios([portfolio])
      }
    }
  }

  function handlePortfolioDoubleClick(portfolio) {
    onEditPortfolio(portfolio)
  }

  function handleSortChange(newSort) {
    window.localStorage.setItem('portfolioSort', newSort)
    setSort(newSort)
  }

  function handleEditClick() {
    onEditPortfolio(selectedPortfolios[0])
  }

  function handleDeletePortfolios() {
    deletePortfolios(selectedPortfolios.map((portfolio) => portfolio.id)).then(
      () => {
        setSelectedPortfolios([])
        setPortfoliosToDelete([])
      },
    )
  }

  function handleDuplicatePortfolio(values) {
    duplicatePortfolio(values).then(() => {
      setPortfolioToDuplicate(false)
    })
  }

  return (
    <div className={clsx('portfolios-frame w-full h-full relative ')} ref={ref}>
      <div
        className={clsx('h-full w-full flex flex-col', {
          'pointer-events-none': lockSelections,
        })}
      >
        <div className="flex items-center p-4">
          <div className="flex-1">
            <div className="flex">
              <SortDropdown value={sort} onChange={handleSortChange} />
            </div>
          </div>
          <div className="search">
            <input
              type="text"
              placeholder="Search portfolios..."
              onChange={(evt) => setSearch(evt.target.value)}
            />
          </div>
        </div>

        {!hideActions && (
          <div className="actions px-6 ">
            {selectedPortfolios.length === 0 ? (
              <div className="buttons">
                <button
                  type="button"
                  className="button bg-blue hover:bg-blue-med"
                  onClick={onNewPortfolio}
                  disabled={disableCreate}
                  {...(disableCreate
                    ? {
                        'data-tooltip':
                          'Free accounts are limited to one portfolio.',
                      }
                    : {})}
                >
                  New Portfolio
                </button>
              </div>
            ) : (
              <div className="flex items-center">
                {selectedPortfolios.length > 0 && (
                  <>
                    <button
                      className="button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark"
                      type="button"
                      key={'unique-button'}
                      onClick={() => setSelectedPortfolios([])}
                    >
                      <CloseIcon fill="#eee" height="24" width="24" />
                    </button>
                    <div className="flex items-center tracking-wide leading-4 ml-2 ">
                      {selectedPortfolios.length} selected
                    </div>
                  </>
                )}
                <>
                  <div className="ml-6"></div>
                  {selectedPortfolios.length === 1 && (
                    <>
                      <button
                        className="button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark ml-2"
                        onClick={handleEditClick}
                        data-tooltip="Edit Portfolio"
                      >
                        <EditIcon fill="#eee" height="18" width="18" />
                      </button>
                      <button
                        className="button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark ml-2"
                        onClick={() =>
                          setPortfolioToDuplicate(selectedPortfolios[0])
                        }
                        data-tooltip="Duplicate Portfolio"
                      >
                        <DuplicateIcon fill="#eee" height="18" width="18" />
                      </button>
                    </>
                  )}

                  <button
                    className={clsx(
                      'button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark ml-2',
                    )}
                    data-tooltip={`Delete Portfolio${
                      selectedPortfolios.length > 1 ? 's' : ''
                    }`}
                    onClick={() => setPortfoliosToDelete(selectedPortfolios)}
                  >
                    <DeleteIcon fill="#eee" height="18" width="18" />
                  </button>
                </>
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex-grow-0 overflow-auto px-6 pb-10">
            <PortfolioList
              portfolios={sortedPortfolios}
              onPortfolioClick={handlePortfolioClick}
              onPortfolioDoubleClick={handlePortfolioDoubleClick}
              selectedPortfolios={selectedPortfolios}
              search={search}
              isLoading={isFetching}
            />
          </div>
        )}
      </div>

      {total > limit && (
        <PaginationBar
          total={total}
          page={page}
          onChange={handlePageChange}
          limit={limit}
        />
      )}

      <GeneralModal
        isOpen={portfoliosToDelete.length > 0}
        onRequestClose={() => {
          setTimeout(() => setSelectedPortfolios(portfoliosToDelete), 0)
          setPortfoliosToDelete([])
        }}
        title="Delete Portfolios"
        buttons={[
          {
            className: 'button bg-red',
            text: 'Delete',
            onClick: handleDeletePortfolios,
          },
        ]}
      >
        <div>
          {portfoliosToDelete.length > 1 ? (
            <div>
              Are you sure you want to delete the following portfolios:
              <ul className="mt-2 mb-2">
                {portfoliosToDelete.map((portfolio) => (
                  <li className="font-bold mt-2" key={portfolio.id}>
                    <h4>{portfolio.name}</h4>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div>
              Are you sure you want to delete{' '}
              <span className="font-bold">{portfoliosToDelete[0]?.name}</span>?
            </div>
          )}
          {deleteLoading && <LoadingBlocker />}
        </div>
      </GeneralModal>

      {!!portfolioToDuplicate ? (
        <PortfolioDuplicateModal
          isOpen={!!portfolioToDuplicate}
          onRequestClose={() => setPortfolioToDuplicate(false)}
          portfolio={portfolioToDuplicate}
          onSubmit={handleDuplicatePortfolio}
          isLoading={duplicatePortfolioIsLoading}
        />
      ) : null}
    </div>
  )
}

const SortDropdown = ({ value, onChange }) => {
  const sorts = [
    {
      id: 'alpha',
      name: 'Alphabetical (A-Z)',
    },
    {
      id: 'alphaReverse',
      name: 'Alphabetical (Z-A)',
    },
    {
      id: 'modified',
      name: 'Last Modified',
    },
  ]
  const currentSort = sorts.find((sort) => sort.id === value)
  const sortedButton = (
    <div className="font-bold text-xs rounded-l py-2 px-6 bg-blue-dark">
      {currentSort?.name}
    </div>
  )
  const defaultButton = (
    <div className="font-bold text-xs rounded h-[32px] pl-6 pr-5 bg-gray-light border border-gray-lighter border-solid flex items-center">
      <span className="inline-block mr-1">Sort By</span>
      <CaretDownIcon />
    </div>
  )
  const dropdownButton = currentSort ? sortedButton : defaultButton
  return (
    <div className="flex">
      <IconMenu icon={dropdownButton} menuPosition="right">
        {sorts.map((sort) => (
          <button onClick={() => onChange(sort.id)} key={sort.id}>
            <span className="inline-block px-2 py-2">{sort.name}</span>
          </button>
        ))}
      </IconMenu>
      {currentSort && (
        <button
          className="font-bold text-xs rounded-r bg-blue-dark"
          onClick={() => onChange(null)}
        >
          <CloseIcon fill="#fff" height="20" />
        </button>
      )}
    </div>
  )
}

function sortPortfolios(portfolios, sort) {
  const sorts = {
    alpha: (a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1
      }
      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1
      }
      return 0
    },
    alphaReverse: (a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return 1
      }
      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return -1
      }
      return 0
    },
    created: (a, b) => {
      if (new Date(a.createdAt).getTime() < new Date(b.createdAt).getTime()) {
        return 1
      }
      if (new Date(a.createdAt).getTime() > new Date(b.createdAt).getTime()) {
        return -1
      }
      return 0
    },
    modified: (a, b) => {
      if (new Date(a.updatedAt).getTime() < new Date(b.updatedAt).getTime()) {
        return 1
      }
      if (new Date(a.updatedAt).getTime() > new Date(b.updatedAt).getTime()) {
        return -1
      }
      return 0
    },
  }
  if (sort) {
    return [...(portfolios || [])].sort(sorts[sort])
  }
  return portfolios
}

export default PortfoliosFrame
